import { CommonButton } from 'components/common/Forms/Button';
import { DatePickerInput } from 'components/common/Forms/DatePickerInput';
import Input from 'components/common/Forms/Input';
import { CSSProperties, ReactElement, useCallback } from 'react';
import { Controller, useForm } from 'react-hook-form';

import { LABEL_BEFORE } from '../../../../styles/fontsStyleUtils';
import {
  SearchPatientWrapper,
  SearchPatientInputWrapper,
  SelectedPatientWrapper,
} from './SearchPatientForm.styled';
import { useCommonTranslation } from '../../../../hooks/i18n/useCommonTranslation';
import { UserData } from '../../../../types/users';

export type SearchPatientFormProps = {
  selectedPatient: UserData | undefined;
  clearSelectedPatient: () => void;
  showPatientSearch: (searchPatientKeys: SearchPatientKeys) => void;
  style?: CSSProperties;
};

export interface SearchPatientKeys {
  lastname?: string;
  birthDate?: string;
}

export const SearchPatientForm = ({
  selectedPatient,
  clearSelectedPatient,
  showPatientSearch,
  style,
}: SearchPatientFormProps): ReactElement => {
  const { t } = useCommonTranslation();

  const {
    register,
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<SearchPatientKeys>({
    // must be present to allow reset it
    defaultValues: {
      lastname: '',
    },
  });

  const submitHandler = useCallback(
    ({ lastname, ...restValues }: SearchPatientKeys) => {
      showPatientSearch({
        // do not send empty string to backend
        lastname: (lastname || '').trim() === '' ? undefined : lastname,
        ...restValues,
      });
      reset();
    },
    [reset, showPatientSearch],
  );

  return (
    <SearchPatientWrapper onSubmit={handleSubmit(submitHandler)} style={style}>
      <div
        style={{
          display: 'flex',
          gap: 'var(--s3)',
          flex: 1,
        }}
      >
        <SearchPatientInputWrapper>
          <Input
            labelPosition={LABEL_BEFORE}
            helperText={t('calendar-surname-helper')}
            label={t('surname')}
            hasError={Boolean(errors.lastname)}
            {...register('lastname')}
          />
        </SearchPatientInputWrapper>
        <SearchPatientInputWrapper>
          <Controller
            control={control}
            name="birthDate"
            render={({ field }) => {
              return (
                <DatePickerInput
                  labelPosition={LABEL_BEFORE}
                  name="birthDate"
                  label={t('date-of-birth')}
                  helperText="DD.MM.YYYY"
                  onChange={(value) => {
                    field.onChange(value);
                  }}
                  id={'patient_birth_date'}
                  value={field.value}
                  hideDateFormatInLabel={true}
                />
              );
            }}
          />
        </SearchPatientInputWrapper>
        <CommonButton variant="primary" type="submit">
          {t('find-patient')}
        </CommonButton>
      </div>
      {Boolean(selectedPatient) && (
        <SelectedPatientWrapper>
          <p>
            {t('selected-patient')}:{' '}
            {selectedPatient?.firstname + ' ' + selectedPatient?.lastname}
          </p>
          <CommonButton
            type="button"
            variant="danger"
            onClick={clearSelectedPatient}
          >
            {t('clear')}
          </CommonButton>
        </SelectedPatientWrapper>
      )}
    </SearchPatientWrapper>
  );
};
